import { For, Show, createSignal } from 'solid-js';
import { Chip } from '~shared/ui/Chip';
import { MemberCard } from './MemberCard';
import { matchedNonMember } from '~/lib/history';
import { snapshotState } from '~/stores/snapshotStore';
import type { LookupResult } from '~/lib/types';

function duplicateOfName(id: string | null): string | null {
  if (!id) return null;
  return snapshotState.index?.byId.get(id)?.member.name ?? 'another record';
}

export function ResultGroup(props: { result: LookupResult }) {
  const r = () => props.result;
  const [viewed, setViewed] = createSignal(false);

  return (
    <section
      class="flex flex-col gap-2"
      aria-label={`Results for ${r().token.name}`}
    >
      <div class="flex flex-wrap items-center justify-between gap-2">
        <div class="flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <h3 class="text-2xl font-bold">{r().token.name}</h3>
          <Show when={r().token.note}>
            <span class="text-muted text-sm">· {r().token.note}</span>
          </Show>
          <Show when={matchedNonMember(r())}>
            <Chip tone="bad">Not a member</Chip>
          </Show>
          <Show when={r().kind === 'none'}>
            <Chip tone="bad">No member found</Chip>
          </Show>
          <Show when={r().kind === 'fuzzy'}>
            <Chip tone="warn">
              No exact match — {r().matches.length} closest shown
            </Chip>
          </Show>
          <Show when={r().kind === 'single-word'}>
            <Chip tone="warn">
              Single name — {r().matches.length + r().truncated} members match
            </Chip>
          </Show>
          <Show when={r().kind === 'exact' && r().matches.length > 1}>
            <Chip tone="warn">
              {r().matches.length} records share this name
            </Chip>
          </Show>
          <Show when={r().truncated > 0}>
            <span class="text-muted text-sm">
              +{r().truncated} more not shown
            </span>
          </Show>
        </div>
        <label class="text-muted flex cursor-pointer items-center gap-1.5 text-sm">
          <input
            type="checkbox"
            checked={viewed()}
            onInput={(e) => setViewed(e.currentTarget.checked)}
          />
          Viewed
        </label>
      </div>

      <Show when={!viewed()}>
        <div class="flex flex-col gap-2 pl-12">
          <Show when={r().kind === 'none'}>
            <p class="text-muted text-sm">
              Nothing in the base matches that name or email.
            </p>
          </Show>
          <For each={r().matches}>
            {(h) => (
              <MemberCard
                history={h}
                duplicateOfName={duplicateOfName(h.member.duplicateOf)}
              />
            )}
          </For>
        </div>
      </Show>
    </section>
  );
}
